import { DateTime } from 'luxon'

import {
  PrimaryTrainingType,
  type SecondaryTrainingType,
  TutorSecondaryTrainingType,
  TutorTrainingSubmissionAssignmentPriority,
  UnsuspendSecondaryTrainingType,
} from '@/queries/constants'
import TrainingQ from '@/queries/TrainingQ'
import TrainingSubmissionQ from '@/queries/TrainingSubmissionQ'
import { type Query, SQL } from '@/client/query'

import type { TrainingUI } from './types'

export const TUTOR_TRAINING_ORDER = SQL`CASE
    WHEN (
        ${TrainingQ.secondary_type} = ${TutorSecondaryTrainingType.ONBOARDING}
    ) THEN 1
    WHEN (
        ${TrainingQ.secondary_type} = ${TutorSecondaryTrainingType.SAT}
    ) THEN 2
    WHEN (
        ${TrainingQ.secondary_type} = ${TutorSecondaryTrainingType.SKILL_BUILDER}
    ) THEN 3
    ELSE 3
    END ASC
`

export const SECONDARY_TRAINING_TYPE_TO_NAME: Record<SecondaryTrainingType, string> = {
  [TutorSecondaryTrainingType.ONBOARDING]: 'ONBOARDING',
  [TutorSecondaryTrainingType.SAT]: 'SAT (NON-BOOTCAMP)',
  [TutorSecondaryTrainingType.SAT_BOOTCAMP]: 'SAT BOOTCAMP',
  [TutorSecondaryTrainingType.SKILL_BUILDER]: 'SKILL BUILDER',
  [TutorSecondaryTrainingType.LEARNING_LABS]: 'LEARNING LABS',
  [TutorSecondaryTrainingType.ENRICHMENT]: 'ENRICHMENT',
  [TutorSecondaryTrainingType.LIVE_HELP]: 'LIVE HELP',
  [TutorSecondaryTrainingType.COLLEGE_ADMISSIONS_WORKSHOP]: 'COLLEGE ADMISSIONS',
  [TutorSecondaryTrainingType.SAT_BOOTCAMP_ONBOARDING]: 'SAT BOOTCAMP ONBOARDING',
  [UnsuspendSecondaryTrainingType.LEARNER_ATTENDANCE]: 'ATTENDANCE POLICY',
}

export const TUTOR_TRAINING_ASSIGNMENT_PRIORITY_TO_NAME: Record<
  TutorTrainingSubmissionAssignmentPriority,
  string
> = {
  [TutorTrainingSubmissionAssignmentPriority.REQUIRED]: 'Required',
  [TutorTrainingSubmissionAssignmentPriority.HIGHLY_RECOMMENDED]: 'Highly Recommended',
}

export function trainingFromQuery({
  training,
  submission,
}: {
  training: Query
  submission: Query | null
}): TrainingUI {
  const submissionId = submission?.(TrainingSubmissionQ.id)
  const submissionSubmittedAt = submission?.(TrainingSubmissionQ.submitted_at) ?? null
  const submissionAssignmentPriority = submission?.(TrainingSubmissionQ.assignment_priority) ?? null
  const submissionAssignmentNote = submission?.(TrainingSubmissionQ.assignment_note) ?? null
  const submissionCreatedAt = submission?.(TrainingSubmissionQ.created_at) ?? null

  const submissionDetails =
    submissionId == null
      ? null
      : {
          id: submissionId,
          submittedAt:
            submissionSubmittedAt == null ? null : DateTime.fromISO(submissionSubmittedAt),
          createdAt: DateTime.fromISO(`${submissionCreatedAt}`),
          assignmentPriority: submissionAssignmentPriority,
          assignmentNote: submissionAssignmentNote,
        }

  return {
    id: training(TrainingQ.id) ?? 0,
    slug: training(TrainingQ.slug) ?? '',
    title: training(TrainingQ.title) ?? '',
    excerpt: training(TrainingQ.excerpt) ?? '',
    description: training(TrainingQ.description) ?? '',
    tagline: training(TrainingQ.tagline) ?? null,
    primaryType: training(TrainingQ.primary_type) ?? PrimaryTrainingType.TUTOR,
    secondaryType: training(TrainingQ.secondary_type) ?? TutorSecondaryTrainingType.ONBOARDING,
    timeEstimateMinutes: training(TrainingQ.time_estimate_minutes) ?? 0,
    link: training(TrainingQ.link_training) ?? '',
    linkSummary: training(TrainingQ.link_summary) ?? '',
    createdAt: training(TrainingQ.created_at) ?? '',
    submission: submissionDetails,
  }
}
